import { useCallback, useEffect, useState } from 'react';
import { useQueueDequeue } from '../contexts/QueueDequeueContext';
import { useQueueDispatchOptional } from '../contexts/QueueDispatchContext';
import {
  enqueueDeparture,
  fetchArrivedNotQueued,
  formatEta,
  fullName,
  parkEntry,
  partyMarker,
  removeEntry,
  removeParty,
  reorderQueue,
  undoConfirmMessage,
  undoLastDequeue,
  unparkEntry,
} from '../services/queue';
import LanguageFlag from './LanguageFlag';
import type { ArrivedDeparture, QueueEntry } from '../types/database';

// Full queue management for the operator: reorder (▲/▼ against the visible
// order — clamped to the timeslot group under the slot_first policy), park /
// unpark ("even weg"), remove with one/party scope, add arrived-but-not-queued
// visitors, and undo. Data mutations go through the same anon RPCs the rest of
// the queue uses; every action ends in queue.refresh() so all surfaces update
// immediately (realtime covers other devices).

interface Props {
  onClose: () => void;
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('nl-NL', {
    timeZone: 'Europe/Amsterdam',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function operatorIdFromSession(): string | null {
  try {
    const raw = sessionStorage.getItem('current_operator');
    return raw ? (JSON.parse(raw).id as string) : null;
  } catch {
    return null;
  }
}

export default function QueueManagePanel({ onClose }: Props) {
  const queue = useQueueDequeue();
  const dispatch = useQueueDispatchOptional();
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [scopeFor, setScopeFor] = useState<QueueEntry | null>(null);
  const [arrived, setArrived] = useState<ArrivedDeparture[] | null>(null);

  // Both queue providers were mounted on the room's location; the dispatch
  // context re-exposes it for the arrived-picker and undo RPCs.
  const locationId = dispatch?.locationId ?? null;

  const loadArrived = useCallback(async () => {
    if (!locationId) {
      setArrived([]);
      return;
    }
    try {
      setArrived(await fetchArrivedNotQueued(locationId));
    } catch (err) {
      console.error('fetchArrivedNotQueued failed', err);
      setArrived([]);
      setNote('Aangekomen bezoekers laden mislukt.');
    }
  }, [locationId]);

  useEffect(() => {
    void loadArrived();
  }, [loadArrived]);

  async function withBusy(fn: () => Promise<void>) {
    setBusy(true);
    setNote(null);
    try {
      await fn();
      queue.refresh();
    } catch (err) {
      setNote(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function move(entry: QueueEntry, delta: -1 | 1) {
    const operatorId = operatorIdFromSession();
    if (!operatorId) return;
    await withBusy(async () => {
      const result = await reorderQueue(entry.queue_entry_id, entry.position + delta, operatorId);
      if (result.clamped) {
        setNote('Niet verplaatst: volgorde is per tijdslot (slot-eerst beleid).');
      }
    });
  }

  async function togglePark(entry: QueueEntry) {
    const operatorId = operatorIdFromSession();
    if (!operatorId) return;
    await withBusy(async () => {
      if (entry.parked) await unparkEntry(entry.queue_entry_id, operatorId);
      else await parkEntry(entry.queue_entry_id, operatorId);
    });
  }

  async function remove(entry: QueueEntry, scope: 'one' | 'party') {
    const operatorId = operatorIdFromSession();
    if (!operatorId) return;
    setScopeFor(null);
    await withBusy(async () => {
      if (scope === 'party') {
        await removeParty(entry.departure_id, operatorId);
      } else {
        await removeEntry(entry.queue_entry_id, operatorId);
      }
      await loadArrived(); // the departure may reappear as addable
    });
  }

  async function add(dep: ArrivedDeparture) {
    const operatorId = operatorIdFromSession();
    if (!operatorId) return;
    await withBusy(async () => {
      await enqueueDeparture(dep.departure_id, operatorId);
      await loadArrived();
    });
  }

  async function undo() {
    const operatorId = operatorIdFromSession();
    if (!operatorId || !locationId) return;
    setBusy(true);
    setNote(null);
    try {
      if (dispatch) {
        let outcome = await dispatch.undoLast();
        if (typeof outcome === 'object') {
          if (!window.confirm(undoConfirmMessage(outcome.predicted))) return;
          outcome = await dispatch.undoLast({ force: true });
          if (typeof outcome === 'object') return;
        }
        setNote(
          outcome === 'undone_with_stop'
            ? 'Teruggezet in de wachtrij en de ervaring is gestopt.'
            : outcome === 'undone'
              ? 'Laatste actie teruggedraaid.'
              : 'Geen recente actie om ongedaan te maken (binnen 5 minuten).',
        );
      } else {
        if (!window.confirm(undoConfirmMessage(null))) return;
        await undoLastDequeue(locationId, operatorId);
        setNote('Laatste actie teruggedraaid.');
        queue.refresh();
      }
      await loadArrived();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setNote(msg.includes('nothing_to_undo')
        ? 'Geen recente actie om ongedaan te maken (binnen 5 minuten).'
        : msg);
    } finally {
      setBusy(false);
    }
  }

  const orderLabel = queue.orderPolicy === 'slot_first' ? 'Volgorde: per tijdslot' : 'Volgorde: check-in';

  return (
    <div className="queue-picker-overlay" onClick={onClose}>
      <div className="queue-picker-modal queue-manage-panel" onClick={(e) => e.stopPropagation()}>
        <header className="queue-picker-header">
          <div className="queue-manage-title">
            <h2>Wachtrij beheren</h2>
            <span
              className="queue-manage-subtitle"
              title={queue.orderPolicy === 'slot_first'
                ? 'Het tijdslot bepaalt de volgorde; verplaatsen kan alleen binnen het eigen tijdslot.'
                : 'Wie het eerst incheckt is het eerst aan de beurt. Verplaatsen kan vrij.'}
            >
              {orderLabel}
            </span>
          </div>
          <div className="queue-manage-header-actions">
            <button
              className="queue-picker-secondary"
              onClick={() => void undo()}
              disabled={busy}
              title="Laatste verwijdering/start terugdraaien (binnen 5 minuten)."
            >
              ↩ Ongedaan maken
            </button>
            <button className="queue-picker-close" onClick={onClose} aria-label="Sluiten">
              ✕
            </button>
          </div>
        </header>

        {note ? <div className="queue-manage-note" onClick={() => setNote(null)}>{note}</div> : null}

        {queue.entries.length === 0 ? (
          <div className="queue-picker-empty">Geen bezoekers in de wachtrij.</div>
        ) : (
          <ul className="queue-picker-list queue-manage-list">
            {queue.entries.map((e, i) => {
              const marker = partyMarker(e);
              return (
                <li key={e.queue_entry_id}>
                  <div className={`queue-picker-row queue-manage-row ${e.parked ? 'queue-picker-row--parked' : ''}`}>
                    <span className="queue-picker-pos">{e.position}</span>
                    <span className="queue-picker-time">{formatTime(e.departure_start)}</span>
                    <span className="queue-picker-label">
                      <LanguageFlag code={e.play_language} className="queue-row-flag" />
                      {fullName(e)}
                      {marker ? ` (${marker})` : ''}
                      {e.experience_names ? ` · ${e.experience_names}` : ''}
                      {e.parked ? (
                        <span className="queue-parked-pill">even weg</span>
                      ) : (
                        <span className="queue-eta">{formatEta(e.eta_minutes)}</span>
                      )}
                    </span>
                    <span className="queue-manage-actions">
                      <button
                        className="queue-manage-btn"
                        disabled={busy || i === 0}
                        onClick={() => void move(e, -1)}
                        aria-label="Omhoog"
                      >
                        ▲
                      </button>
                      <button
                        className="queue-manage-btn"
                        disabled={busy || i === queue.entries.length - 1}
                        onClick={() => void move(e, 1)}
                        aria-label="Omlaag"
                      >
                        ▼
                      </button>
                      <button
                        className={`queue-manage-btn ${e.parked ? 'queue-manage-unpark' : 'queue-manage-park'}`}
                        disabled={busy}
                        onClick={() => void togglePark(e)}
                        title={e.parked ? 'Bezoeker is terug' : 'Bezoeker is even weg, plek blijft behouden'}
                      >
                        {e.parked ? 'Terug' : 'Even weg'}
                      </button>
                      <button
                        className="queue-manage-btn queue-manage-remove"
                        disabled={busy}
                        onClick={() =>
                          e.party_size > 1 ? setScopeFor(e) : void remove(e, 'one')
                        }
                        aria-label="Verwijderen"
                      >
                        ✕
                      </button>
                    </span>
                  </div>

                  {scopeFor?.queue_entry_id === e.queue_entry_id ? (
                    <div className="queue-manage-scope">
                      <span>Wie verwijderen?</span>
                      <button
                        className="queue-picker-secondary"
                        disabled={busy}
                        onClick={() => void remove(e, 'one')}
                      >
                        Alleen {fullName(e)}{marker ? ` (${marker})` : ''}
                      </button>
                      <button
                        className="queue-picker-secondary"
                        disabled={busy}
                        onClick={() => void remove(e, 'party')}
                      >
                        Hele gezelschap ({e.party_size})
                      </button>
                      <button
                        className="queue-picker-secondary"
                        onClick={() => setScopeFor(null)}
                      >
                        Annuleren
                      </button>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}

        <div className="queue-manage-add">
          <h3>Aangekomen, nog niet in de wachtrij</h3>
          {arrived === null ? (
            <div className="queue-picker-empty">Laden…</div>
          ) : arrived.length === 0 ? (
            <div className="queue-picker-empty">Geen aangekomen bezoekers buiten de wachtrij.</div>
          ) : (
            <ul className="queue-picker-list queue-manage-list">
              {arrived.map((d) => (
                <li key={d.departure_id}>
                  <div className="queue-picker-row queue-manage-row">
                    <span className="queue-picker-time">{formatTime(d.departure_start)}</span>
                    <span className="queue-picker-label">
                      {fullName(d)}
                      {d.party_size && d.party_size > 1 ? ` · ${d.party_size} pers.` : ''}
                      {d.experience_names ? ` · ${d.experience_names}` : ''}
                    </span>
                    <span className="queue-manage-actions">
                      <button
                        className="queue-manage-btn queue-manage-add-btn"
                        disabled={busy}
                        onClick={() => void add(d)}
                      >
                        + Voeg toe
                      </button>
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <footer className="queue-picker-footer">
          <button className="queue-picker-primary" onClick={onClose}>
            Sluiten
          </button>
        </footer>
      </div>
    </div>
  );
}
